import type { VinDetail } from '@packages/models/api';

import { useCallback, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { createUseStyles } from 'react-jss';

import type { FailCodeModalContent, FailCodeSearchMethod } from '../fail-code-section.types';
import type { FailCodeSelection, FailCodeVehicleParams } from './fail-codes-modal.types';

import { FailCodeSearchMethodSelector } from './fail-code-search-method-selector';
import { LookupByElectricalGridLocation } from './lookup-by-electrical-grid-location/lookup-by-electrical-grid-location';
import { LookupBySection } from './lookup-by-section';
import { NoTroubleFound } from './no-trouble-found';
import { SearchByFailCode } from './search-by-fail-code';
import { SearchByPart } from './search-by-part';

export type {
    AnalyticalProp,
    ContentTypes,
    FailCodeContainer,
    FailCodeModalContent,
    FailCodeSearchMethod,
    SearchProps,
} from '../fail-code-section.types';

const useFailCodeSearchModalStyles = createUseStyles({
    failCodeSearchModal: {
        maxWidth: '728px',
        '@media (min-width: 597px)': {
            minWidth: '728px',
        },
    },
});
/**
 * Props for the {@link FailCodesModal} component.
 */
export type FailCodesModalProps = {
    /**
     * Callback invoked when the user confirms a fail code selection from any search method.
     * Receives the completed {@link FailCodeSelection} to persist on the techline case.
     */
    onConfirmFailCode: (selection: FailCodeSelection) => void;

    /** Callback invoked when the modal should close (e.g. clicking the X or pressing escape). */
    onClose: () => void;

    /** Whether the modal is currently visible. */
    show: boolean;

    /**
     * Vehicle VIN data from the case's VIN decode response. This component maps the VIN
     * fields to {@link FailCodeVehicleParams} (renaming `modelNumber` -> `modelCode`) and
     * passes the result to each search method child.
     */
    vehicleVinData: Pick<VinDetail, 'modelNumber' | 'modelYear' | 'optionCode' | 'vin'>;
};

/**
 * Top-level fail code search modal. Acts as a router between the search method selector
 * and the five search method components (lookup by section, search by fail code, search by part,
 * lookup by electrical grid, and no trouble found).
 *
 * When the modal opens, the user sees the search method selector. After choosing a method,
 * the modal switches to the corresponding search component. Each child component receives
 * `onConfirmFailCode`, `onBack`, and `onClose` callbacks; confirming a selection bubbles
 * the {@link FailCodeSelection} up to this modal's parent.
 *
 * @param props - {@link FailCodesModalProps}
 * @returns the fail codes modal JSX
 */
export function FailCodesModal({ onClose, onConfirmFailCode, show, vehicleVinData }: FailCodesModalProps) {
    const classes = useFailCodeSearchModalStyles();
    // State
    const [content, setContent] = useState<FailCodeModalContent>('start');

    // Derived state

    // Map VIN decode field names to the fail code API's field names once, here.
    // Children receive FailCodeVehicleParams directly and never see `modelNumber`.
    const vehicleParams: FailCodeVehicleParams = useMemo(
        () => ({
            modelCode: vehicleVinData?.modelNumber,
            modelYear: vehicleVinData?.modelYear != null ? String(vehicleVinData.modelYear) : undefined,
            optionCode: vehicleVinData?.optionCode,
            vin: vehicleVinData?.vin,
        }),
        [vehicleVinData?.modelNumber, vehicleVinData?.modelYear, vehicleVinData?.optionCode, vehicleVinData?.vin]
    );

    // Handlers

    /**
     * Navigate back to the search method selector. Called by child components via onBack.
     */
    const handleBack = useCallback(() => {
        setContent('start');
    }, []);

    /**
     * Close the modal and reset the content to the start screen so re-opening starts fresh.
     */
    const handleClose = useCallback(() => {
        setContent('start');
        onClose();
    }, [onClose]);

    /**
     * Handle the user selecting a search method from the selector. Navigates the modal
     * to the corresponding search method panel.
     *
     * @param method - the selected search method
     */
    const handleSearchMethodSelect = useCallback((method: FailCodeSearchMethod) => {
        setContent(method);
    }, []);

    return (
        <Modal centered dialogClassName={classes.failCodeSearchModal} onHide={handleClose} show={show}>
            {content === 'start' && (
                <FailCodeSearchMethodSelector
                    onClose={handleClose}
                    onSearchMethodSelect={handleSearchMethodSelect}
                    show={show && content === 'start'}
                />
            )}
            {content === 'lookupBySection' && (
                <LookupBySection
                    onBack={handleBack}
                    onClose={handleClose}
                    onConfirmFailCode={onConfirmFailCode}
                    vehicleParams={vehicleParams}
                />
            )}
            {content === 'searchByFailCode' && (
                <SearchByFailCode
                    onBack={handleBack}
                    onClose={handleClose}
                    onConfirmFailCode={onConfirmFailCode}
                    vehicleParams={vehicleParams}
                />
            )}
            {content === 'searchByPart' && (
                <SearchByPart
                    onBack={handleBack}
                    onClose={handleClose}
                    onConfirmFailCode={onConfirmFailCode}
                    vehicleParams={vehicleParams}
                />
            )}
            {content === 'lookupByElectricalGrid' && (
                <LookupByElectricalGridLocation
                    onBack={handleBack}
                    onClose={handleClose}
                    onConfirmFailCode={onConfirmFailCode}
                    vehicleParams={vehicleParams}
                />
            )}
            {content === 'noTroubleFound' && (
                <NoTroubleFound onBack={handleBack} onClose={handleClose} onConfirmFailCode={onConfirmFailCode} />
            )}
        </Modal>
    );
}
